import { FC, useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";

import {
  FocusTurnView,
  useEventNodes,
  useFocusLaneScope,
  useFocusSetParams,
  useFocusTurnNavigation,
} from "@tsmono/inspect-components/transcript";
import { NoContentsPanel } from "@tsmono/react/components";
import { navigateAndForget } from "@tsmono/react/hooks";

import { useAppConfig } from "../../../app_config";
import {
  kLogViewSamplesTabId,
  kSampleTranscriptTabId,
} from "../../../constants";
import { setDocumentTitle } from "../../../state/actions";
import {
  useSelectedEvalSampleData,
  useSelectedLogDetails,
} from "../../../state/hooks";
import { useLogSampleNavigationActions } from "../../routing/sampleNavigation";
import {
  logsUrl,
  sampleEventUrl,
  samplesUrl,
  toFullUrlMaybe,
  useLogOrSampleRouteParams,
  useRoutePrefix,
  useSampleUrlBuilder,
} from "../../routing/url";
import { SampleNavbar } from "../SampleNavbar";

import styles from "./SampleEventView.module.css";

/**
 * Focus-mode page (single focused turn, entered from a transcript event).
 * Renders the focused event (and its turn's tool calls) fully expanded, with
 * the transcript's own renderer but without the list's card/gutter chrome.
 * URL pattern: /logs/<logPath>/samples/sample/<id>/<epoch>/event?event=<eventId>
 * (also mounted under /tasks and /samples so back/home return to the surface
 * the sample was opened from).
 */
export const SampleEventView: FC = () => {
  const { logPath, id: sampleId, epoch } = useLogOrSampleRouteParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const eventId = searchParams.get("event");
  const prefix = useRoutePrefix();
  const location = useLocation();
  const isSamplesSurface = location.pathname.startsWith("/samples/");

  const sampleData = useSelectedEvalSampleData();
  const sample = sampleData.sample;
  const sampleLoading =
    sampleData.status === "loading" || sampleData.status === "streaming";

  // Focus mode is designed for open-in-new-tab, where the tab title is all
  // the user sees — same title as the sample view (SampleDisplay).
  const logDetails = useSelectedLogDetails();
  const evalSpec = logDetails?.eval;
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    setDocumentTitle({ evalSpec, sample });
  }, [evalSpec, sample]);

  // A running sample has no settled `sample` until it completes — its events
  // stream through `running` instead. Read the same source the transcript
  // view renders from, so focus mode works while the sample runs.
  const runningEvents = sampleData.running;
  const isRunning = !sample && runningEvents.length > 0;

  // Swimlane scope for the focused event, from the transcript's own timeline
  // machinery — the event tree below is built from the SAME scoped events the
  // transcript renders for that lane, so turn numbers match the main view.
  const scope = useFocusLaneScope(
    sample?.events ?? runningEvents,
    eventId,
    sample?.timelines ?? undefined
  );
  // The full list keeps uuid-less node ids identical to the transcript's, so
  // focus links and `?event=` resolve here too.
  const { eventNodes, defaultCollapsedIds } = useEventNodes(
    scope.laneEvents,
    isRunning,
    undefined,
    sample?.events ?? runningEvents
  );
  // Sample stepping for the navbar arrows (which own the ArrowLeft /
  // ArrowRight binding — see SampleNavbar).
  const sampleNavigation = useLogSampleNavigationActions();
  const setParams = useFocusSetParams(setSearchParams);
  const following = searchParams.get("follow") === "1";
  const nav = useFocusTurnNavigation(
    eventNodes,
    eventId,
    searchParams.get("tab") ?? "Summary",
    setParams,
    defaultCollapsedIds,
    scope,
    {
      running: isRunning,
      following,
    }
  );

  // Prev/next sample stays on the focus page: a parameterless `event` URL on
  // the sibling resolves to its first turn (see useFocusTurnNavigation).
  const { singleFileMode } = useAppConfig();

  // Exit focus mode back to the normal transcript, deep-linked at the focused
  // event (`?event=`), so the transcript opens scrolled to the same turn.
  const navigate = useNavigate();
  const builder = useSampleUrlBuilder();
  const { resolvedEventId } = nav;
  const exitUrl = useMemo(() => {
    if (!logPath) return undefined;
    const base = resolvedEventId
      ? sampleEventUrl(builder, resolvedEventId, logPath, sampleId, epoch)
      : builder(logPath, sampleId, epoch, kSampleTranscriptTabId);
    // Exiting while following returns to a tailing transcript (follow wins over
    // ?event= per the transcript's seed precedence), mirroring `f`-into-focus.
    return following
      ? `${base}${base.includes("?") ? "&" : "?"}follow=1`
      : base;
  }, [logPath, sampleId, epoch, resolvedEventId, builder, following]);
  const onExit = useCallback(() => {
    if (!exitUrl) return;
    navigateAndForget(navigate, exitUrl);
  }, [exitUrl, navigate]);
  const homeUrl = useMemo(() => {
    if (!logPath) return undefined;
    return isSamplesSurface
      ? samplesUrl(logPath)
      : logsUrl(logPath, undefined, kLogViewSamplesTabId, prefix);
  }, [isSamplesSurface, logPath, prefix]);

  // Mirror LogSampleDetailView: append "/sample" so the log file is a clickable
  // breadcrumb segment. Crumbs resolve back into the surface the sample was
  // opened from: the samples grid on /samples, the log's samples tab otherwise.
  const fnNavigationUrl = useCallback(
    (file: string, log_dir?: string) => {
      if (isSamplesSurface) {
        const normalizedFile = file.endsWith("/") ? file.slice(0, -1) : file;
        const target =
          logPath &&
          (normalizedFile === logPath || normalizedFile === `${logPath}/sample`)
            ? logPath
            : file;
        return samplesUrl(target, log_dir);
      }
      if (!logPath || !file) {
        return logsUrl(file, log_dir, undefined, prefix);
      }
      const normalizedFile = file.endsWith("/") ? file.slice(0, -1) : file;
      if (
        normalizedFile === logPath ||
        normalizedFile === `${logPath}/sample`
      ) {
        return logsUrl(logPath, log_dir, kLogViewSamplesTabId, prefix);
      }
      return logsUrl(file, log_dir, undefined, prefix);
    },
    [isSamplesSurface, logPath, prefix]
  );

  // A sample-level error (eval error / limit cancellation) is a property of
  // the whole sample — FocusTurnView surfaces it on every focused turn.
  const sampleError = sample?.error;
  const header = (
    <SampleNavbar
      sampleId={sampleId}
      epoch={epoch}
      navigation={sampleNavigation}
      navbarConfig={{
        currentPath: logPath ? `${logPath}/sample` : undefined,
        fnNavigationUrl,
        // Back exits focus mode (the previous state), landing on the
        // transcript deep-linked at the focused turn.
        backUrl: exitUrl,
        homeUrl,
        bordered: true,
        breadcrumbsEnabled: !singleFileMode,
      }}
      loading={sampleLoading}
    />
  );

  if (!sample && runningEvents.length === 0) {
    return (
      <div className={styles.root}>
        {header}
        <div className={styles.loading}>
          loading sample data
          <span className={styles.ellipsis} />
        </div>
      </div>
    );
  }

  if (nav.slice.length === 0) {
    // While streaming, the focused turn may simply not have arrived yet.
    return (
      <div className={styles.root}>
        {header}
        {isRunning ? (
          <div className={styles.loading}>
            loading sample data
            <span className={styles.ellipsis} />
          </div>
        ) : (
          <NoContentsPanel text="Event not found in this sample." />
        )}
      </div>
    );
  }

  return (
    <FocusTurnView
      nav={nav}
      eventId={eventId}
      header={header}
      error={
        sampleError
          ? { label: "Sample error", message: sampleError.message }
          : undefined
      }
      onExit={onExit}
      exitHref={toFullUrlMaybe(exitUrl)}
    />
  );
};
